import { Flex } from 'components/flex';
import { formatDate } from 'utils/date';
import { Row, Col } from 'components/grid';
import { SectionHeader } from 'components/section-header';
import { InputField, SwitchField } from 'components/fields';
import {
  PayrollSettingChange,
  usePayrollSettingsQuery,
} from 'store/api/company';

import * as Styles from './styles';

type PercentKey = 'tipFeePercent' | 'brokenMissingPercent';
type ToggleKey = 'isTipFeeEnabled' | 'isBrokenMissingEnabled';

const describeChange = (change?: PayrollSettingChange) => {
  if (!change) {
    return undefined;
  }
  const when = formatDate(change.createdAt, 'MMM D, YYYY h:mm A');
  if (change.setting.endsWith('Percent')) {
    return `Last changed ${when}: ${Number(change.oldValue)}% → ${Number(change.newValue)}%`;
  }
  return `Last changed ${when}: turned ${change.newValue === 'true' ? 'on' : 'off'}`;
};

const settings: {
  help: string;
  title: string;
  label: string;
  toggle: ToggleKey;
  percent: PercentKey;
}[] = [
  {
    title: 'Tip fee',
    label: 'Tip fee %',
    percent: 'tipFeePercent',
    toggle: 'isTipFeeEnabled',
    help: 'Reps are paid their tip share minus this %. They see the reduced tip only.',
  },
  {
    label: 'Broken & missing %',
    percent: 'brokenMissingPercent',
    toggle: 'isBrokenMissingEnabled',
    title: 'Broken & missing products',
    help: 'Each payout gets a "Broken & missing products" deduction: this % of the rep\'s net sales for the event (gross ÷ (1 + tax + card fee), tips excluded).',
  },
];

/**
 * Owner, 17 Oct 2026: two payroll settings. They apply to unpaid payouts
 * and everything after; paid payouts never change.
 */
export const PayrollSettingsSection = ({
  companyId,
}: {
  companyId?: number;
}) => {
  const { data } = usePayrollSettingsQuery(String(companyId), {
    skip: !companyId,
  });

  const lastChange = (keys: string[]) =>
    data?.history?.find((change) => keys.includes(change.setting));

  return (
    <>
      <SectionHeader
        title="Payroll"
        withBorder={false}
        subTitle="Applies to payouts not paid yet and to everything after. Paid payouts never change."
      />
      {settings.map(({ help, title, label, toggle, percent }) => {
        const change = describeChange(lastChange([toggle, percent]));
        return (
          <Row key={toggle}>
            <Col col={4}>
              <Flex gap={8} flexDirection="column">
                {/* Owner 17 Oct: one row per setting, "input | toggle". */}
                <Styles.InlineSetting>
                  <Styles.InlineInput>
                    <InputField
                      number
                      decimal
                      label={label}
                      name={percent}
                      placeholder={label}
                    />
                  </Styles.InlineInput>
                  <Styles.InlineToggle data-testid={`${toggle}-toggle`}>
                    <SwitchField name={toggle} aria-label={title} />
                  </Styles.InlineToggle>
                </Styles.InlineSetting>
                {change && (
                  <Styles.SwitchSubtitle data-testid={`${toggle}-last-change`}>
                    {change}
                  </Styles.SwitchSubtitle>
                )}
                <Styles.SwitchSubtitle>{help}</Styles.SwitchSubtitle>
              </Flex>
            </Col>
          </Row>
        );
      })}
    </>
  );
};
